import { BenefitStatus } from '@zorro/clients';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Stack } from '@zorro/web/design-system';
import { useBatchCallEndpoint, useIdempotencyKey } from '@zorro/web/ui-utils';
import { useMemo } from 'react';

import { FormFooter } from '../../FormFooter';
import { InformationBoxComponent } from '../../InformationBox';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

type FlagAsApplicationSentFormProps = {
  recordsForActions: RecordForActions[];
  onClose: () => void;
  onSuccess: () => void;
};

const FlagAsApplicationSentForm = ({
  recordsForActions,
  onClose,
  onSuccess,
}: FlagAsApplicationSentFormProps) => {
  const idempotencyKey = useIdempotencyKey();

  const {
    executeBatchCall: executeBatchCallBenefitStatus,
    errors: errorsBenefits,
  } = useBatchCallEndpoint({
    methodName: 'benefitsControllerUpdateBenefitStatus',
    singularItemName: 'benefit',
    action: 'updated',
    batchSize: 5,
  });

  async function handleSubmit() {
    const batchItemsBenefitStatus = recordsForActions.map(
      ({ fullName, benefitId }) => ({
        key: fullName,
        params: [
          benefitId as string,
          idempotencyKey.getFor(benefitId as string),
          { status: BenefitStatus.CARRIER_APPLICATION_SENT },
        ] as const,
      })
    );

    await executeBatchCallBenefitStatus(batchItemsBenefitStatus, {
      onSuccess,
    });
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Flag as application sent"
        primaryButtonProps={{ onClick: handleSubmit }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <InformationBoxComponent
        title="Please note"
        items={[
          "It's recommended to submit applications through individual employee pages where all details can be verified",
          "When flagging in bulk, application-related fields remain at default values. It's your responsibility to confirm/update premium amounts, collect application evidence, and update payment status on the individual enrollment pages",
        ]}
      />

      <ErrorsTable
        tableTitle="Employee name"
        errors={errorsBenefits}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};

export { FlagAsApplicationSentForm };
